import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Users, Building2, CreditCard, Activity, Ban, CheckCircle2, Archive, Copy } from "lucide-react";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [archiveMeta, setArchiveMeta] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch { setStats(null); }
    })();
  }, []);

  if (!stats) return <div className="text-subtle">Chargement des statistiques…</div>;

  return (
    <div data-testid="admin-overview">
      <h2 className="font-display text-2xl font-black text-ink mb-4">Vue d'ensemble</h2>

      <section className="mb-8">
        <p className="overline mb-3">Comptes</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card icon={<Users size={14} />} label="Utilisateurs" value={stats.users.total} />
          <Card icon={<Building2 size={14} />} label="Établissements" value={stats.commerces.total} />
          <Card icon={<CheckCircle2 size={14} className="text-[#0A7F2E]" />} label="Étab. actifs" value={stats.commerces.active} />
          <Card icon={<Ban size={14} className="text-destructive" />} label="Bloqués" value={stats.users.blocked + stats.commerces.blocked} />
        </div>
      </section>

      <section>
        <p className="overline mb-3">Paiements & Abonnements</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card icon={<CreditCard size={14} />} label="Paiements réussis" value={stats.payments.countPaid} />
          <Card icon={<Activity size={14} />} label="Montant encaissé" value={`${stats.payments.totalAmountEur.toFixed(2)} €`} />
          <Card icon={<CheckCircle2 size={14} />} label="Abonnements actifs" value={stats.payments.subscriptionsActive} />
          <Card icon={<Activity size={14} />} label="Revenus abonnements" value={`${stats.payments.subscriptionRevenueEur.toFixed(2)} €`} />
        </div>
      </section>

      <section className="mt-10 border border-border bg-surface/40 p-5">
        <p className="overline mb-2">Protection intellectuelle (INPI)</p>
        <h3 className="font-display text-lg font-bold text-ink">Dossier de dépôt e-Soleau</h3>
        <p className="mt-1 text-sm text-subtle leading-relaxed">
          Téléchargez un dossier PDF complet et horodaté : description fonctionnelle, architecture, modèle de données, endpoints, flux métier, innovations, paramétrages, statistiques live. Document prêt à compléter avec vos captures d'écran et à déposer sur le service e-Soleau de l'INPI (15 €/an).
        </p>
        <button
          onClick={async () => {
            const token = localStorage.getItem("bypassgo_token");
            const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
            try {
              const res = await fetch(`${apiBase}/admin/inpi-dossier.pdf`, { headers: { Authorization: `Bearer ${token}` } });
              if (!res.ok) throw new Error("dl");
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `BypassGo-INPI-${new Date().toISOString().slice(0, 10)}.pdf`;
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
            } catch { /* ignore */ }
          }}
          className="mt-4 inline-flex items-center gap-2 h-11 px-5 bg-ink text-white font-bold text-sm hover:bg-ink/90"
          data-testid="download-inpi-pdf-btn"
        >
          <Activity size={14} /> Télécharger le dossier INPI (PDF)
        </button>
      </section>

      <section className="mt-6 border border-border bg-surface/40 p-5" data-testid="source-archive-section">
        <p className="overline mb-2">Code source — Dépôt de preuve</p>
        <h3 className="font-display text-lg font-bold text-ink">Archive complète du code source (frontend + backend)</h3>
        <p className="mt-1 text-sm text-subtle leading-relaxed">
          Génère une archive ZIP du code source à un instant T, avec un fichier <code className="font-mono text-xs bg-white px-1">MANIFEST.txt</code> listant chaque fichier et son SHA-256, plus un SHA-256 global de l'archive. Idéal pour constituer un dépôt de preuve INPI / huissier / horodatage blockchain. Les secrets (<code className="font-mono text-xs bg-white px-1">.env</code>) et dépendances (<code className="font-mono text-xs bg-white px-1">node_modules</code>, caches) sont exclus.
        </p>

        {archiveMeta && (
          <div className="mt-4 border border-border bg-white p-4 font-mono text-xs space-y-2" data-testid="archive-meta">
            <div className="flex justify-between gap-4"><span className="text-subtle">Fichiers</span><span className="font-bold text-ink">{archiveMeta.files}</span></div>
            <div className="flex justify-between gap-4"><span className="text-subtle">Taille</span><span className="font-bold text-ink">{(archiveMeta.size_bytes / 1024).toFixed(1)} Ko</span></div>
            <div className="flex justify-between gap-4"><span className="text-subtle">Généré le</span><span className="font-bold text-ink">{new Date(archiveMeta.generated_at).toLocaleString("fr-FR")}</span></div>
            <div className="pt-2 border-t border-border">
              <div className="text-subtle text-[10px] uppercase tracking-wider mb-1">SHA-256 de l'archive (à horodater)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all text-[11px] text-ink select-all" data-testid="archive-sha256">{archiveMeta.sha256_archive}</code>
                <button
                  onClick={() => navigator.clipboard?.writeText(archiveMeta.sha256_archive)}
                  className="shrink-0 inline-flex items-center gap-1 h-7 px-2 border border-border text-[10px] font-bold hover:bg-surface"
                  data-testid="copy-archive-sha-btn"
                  title="Copier"
                >
                  <Copy size={11} /> Copier
                </button>
              </div>
            </div>
            <div className="pt-1">
              <div className="text-subtle text-[10px] uppercase tracking-wider mb-1">SHA-256 manifeste (somme déterministe des fichiers)</div>
              <code className="break-all text-[11px] text-subtle select-all">{archiveMeta.sha256_global}</code>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-4">
          <button
            onClick={async () => {
              setArchiveLoading(true);
              const token = localStorage.getItem("bypassgo_token");
              const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
              try {
                const res = await fetch(`${apiBase}/admin/source-archive.zip`, { headers: { Authorization: `Bearer ${token}` } });
                if (!res.ok) throw new Error("dl");
                const sha = res.headers.get("X-Archive-Sha256");
                const manifestSha = res.headers.get("X-Manifest-Sha256");
                const fileCount = res.headers.get("X-File-Count");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `BypassGo-SourceCode-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.zip`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                if (sha) {
                  setArchiveMeta({
                    files: Number(fileCount) || 0,
                    size_bytes: blob.size,
                    sha256_archive: sha,
                    sha256_global: manifestSha || "",
                    generated_at: new Date().toISOString(),
                  });
                }
              } catch { /* ignore */ }
              setArchiveLoading(false);
            }}
            disabled={archiveLoading}
            className="inline-flex items-center gap-2 h-11 px-5 bg-ink text-white font-bold text-sm hover:bg-ink/90 disabled:opacity-50"
            data-testid="download-source-archive-btn"
          >
            <Archive size={14} /> {archiveLoading ? "Génération en cours…" : "Télécharger l'archive ZIP"}
          </button>
          <button
            onClick={async () => {
              setArchiveLoading(true);
              try {
                const { data } = await api.get("/admin/source-archive/info");
                setArchiveMeta(data);
              } catch { /* ignore */ }
              setArchiveLoading(false);
            }}
            disabled={archiveLoading}
            className="inline-flex items-center gap-2 h-11 px-5 border border-ink text-ink font-bold text-sm hover:bg-surface disabled:opacity-50"
            data-testid="compute-source-sha-btn"
          >
            <Activity size={14} /> Calculer SHA-256 sans télécharger
          </button>
        </div>
      </section>
    </div>
  );
}

function Card({ icon, label, value }) {
  return (
    <div className="border border-border p-4 bg-white" data-testid={`stat-${String(label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center gap-2 text-xs text-subtle uppercase tracking-wider font-bold">{icon} {label}</div>
      <p className="mt-2 font-mono text-3xl font-bold text-ink">{value}</p>
    </div>
  );
}
